import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { Download } from 'lucide-react';
import { 
  FaArrowLeft,
  FaArrowRight,
  FaPlus,
  FaTimes
} from 'react-icons/fa';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUserDetails } from '../../../actions/userActions';
import GradientButton from '../../../components/GradientButton';
import CoverLetterProgress from '../../../components/CoverLetterProgress';

function CoverLetter() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const defaultFormData = {
    // Dane osobowe - będą pobrane z profilu
    fullName: '',
    address: '',
    city: '',
    phone: '', 
    email: '', 
    // Dane pracodawcy
    companyName: '',
    employerName: '',
    position: '',
    // Wybór zawodu
    profession: '',
    customProfession: '',
    customCompetencies: ['', '', '', ''],
    // Dodatkowe informacje
    practicePlace: '',
    achievements: '',
    motivation: ''
  };

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(defaultFormData);

  const userDetails = useSelector(state => state.userDetails);
  const { user } = userDetails;

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Funkcja która wypełnia pola danymi z profilu (jeśli są)
  const populatePersonalData = useCallback((u) => {
    if (!u) return;
    setFormData(prev => ({
      ...prev,
      fullName: `${u.name || ''} ${u.last_name || ''}`.trim(),
      address: u.address || '',
      city: u.city || '',
      phone: u.phone || '', // ZMIANA
      email: u.email || ''  // ZMIANA
    }));
  }, []);

  // Pobierz dane użytkownika przy montowaniu komponentu
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
      return;
    }
    if (!user || !user.name) {
      dispatch(getUserDetails('profile'));
    } else {
      populatePersonalData(user);
    }
  }, [dispatch, userInfo, user, navigate, populatePersonalData]);

  // Reset form i ponowne uzupełnienie z profilu (jeśli istnieje)
  const resetForm = () => {
    setFormData(defaultFormData);
    if (user && user.name) {
      populatePersonalData(user);
    }
    setStep(1);
  };

  const professions = [
    { value: 'cukiernik', label: 'Cukiernik', icon: '🍰' },
    { value: 'mechanik', label: 'Mechanik pojazdów samochodowych', icon: '🚗' },
    { value: 'krawiec', label: 'Krawiec', icon: '✂️' },
    { value: 'sprzedawca', label: 'Sprzedawca', icon: '🛒' },
    { value: 'stolarz', label: 'Stolarz', icon: '🪚' },
    { value: 'tapicer', label: 'Tapicer', icon: '🛋️' },
    { value: 'piekarz', label: 'Piekarz', icon: '🥖' },
    { value: 'kucharz', label: 'Kucharz', icon: '👨‍🍳' },
    { value: 'fryzjer', label: 'Fryzjer', icon: '💇' },
    { value: 'hydraulik', label: 'Monter instalacji sanitarnych', icon: '🔧' },
    { value: 'elektryk', label: 'Elektryk', icon: '⚡' },
    { value: 'murarz', label: 'Murarz', icon: '🧱' },
    { value: 'monter', label: 'Monter zabudowy i robót wykończeniowych', icon: '🏗️' },
    { value: 'magazynier', label: 'Magazynier', icon: '📦' },
    { value: 'operator_maszyn', label: 'Operator maszyn rolniczych', icon: '🚜' },
    { value: 'rolnik', label: 'Rolnik', icon: '🌾' },
    { value: 'rybak', label: 'Rybak', icon: '🎣' },
    { value: 'custom', label: 'Inna profesja', icon: '✨' }
  ];

  const competencies = {
    cukiernik: [
      'Kompleksowe przygotowywanie różnorodnych ciast, tortów i ciasteczek',
      'Dekorowanie i wykańczanie wyrobów (worki cukiernicze, masy)',
      'Znajomość zasad higieny i bezpieczeństwa żywności (HACCP)',
      'Precyzyjne ważenie i dozowanie składników'
    ],
    mechanik: [
      'Podstawy diagnostyki i przeglądów technicznych',
      'Wymiana i naprawa elementów układu hamulcowego i zawieszenia',
      'Obsługa podstawowych narzędzi warsztatowych',
      'Znajomość zasad BHP w warsztacie'
    ],
    krawiec: [
      'Obsługa maszyn szwalniczych różnego typu',
      'Dopasowywanie i obróbka tkanin',
      'Umiejętne gospodarowanie powierzoną tkaniną',
      'Wyobraźnia i zmysł estetyczny w przygotowywaniu wyrobów'
    ],
    sprzedawca: [
      'Obsługa kasy fiskalnej',
      'Obsługa klienta zwłaszcza trudnego i wymagającego',
      'Przygotowywanie oryginalnych ekspozycji wystawowych',
      'Znajomość technik sprzedaży'
    ],
    stolarz: [
      'Znajomość materiałów (gatunki drewna, okucia)',
      'Obsługa maszyn i narzędzi (piły, wiertarki)',
      'Precyzja i dbałość o detale w obróbce drewna',
      'Praca z materiałami drewnopochodnymi'
    ],
    tapicer: [
      'Znajomość materiałów (drewno, tkaniny, okucia)',
      'Obsługa narzędzi (piły, wiertarki, zszywacze tapicerskie)',
      'Precyzja w piankowania i obijania mebli',
      'Dbałość o jakość produktu finalnego'
    ],
    piekarz: [
      'Podstawowa znajomość receptur i technik piekarskich',
      'Higiena pracy (HACCP)',
      'Zmysł estetyczny w prezentacji wyrobów',
      'Efektywne gospodarowanie składnikami'
    ],
    kucharz: [
      'Podstawowa znajomość receptur i technik kulinarnych',
      'Higiena pracy (HACCP)',
      'Zmysł estetyczny w prezentacji dań',
      'Efektywne gospodarowanie składnikami'
    ],
    fryzjer: [
      'Podstawowe techniki strzyżenia, koloryzacji i pielęgnacji',
      'Komunikatywność i umiejętność słuchania klienta',
      'Zmysł estetyczny i wyczucie trendów',
      'Obsługa profesjonalnych narzędzi fryzjerskich'
    ],
    hydraulik: [
      'Montaż i naprawy instalacji sanitarnych',
      'Czytanie rysunku technicznego',
      'Dyscyplina w zakresie BHP',
      'Obsługa narzędzi specjalistycznych'
    ],
    elektryk: [
      'Montaż i naprawy instalacji elektrycznych',
      'Czytanie rysunku technicznego',
      'Dyscyplina w zakresie BHP',
      'Pomiary i diagnostyka instalacji'
    ],
    murarz: [
      'Znajomość technologii i kolejności prac budowlanych',
      'Dokładność w pomiarach i poziomowaniu',
      'Przygotowanie stanowiska pracy',
      'Mieszanie zapraw budowlanych'
    ],
    monter: [
      'Znajomość technologii prac wykończeniowych',
      'Glazurnictwo, malowanie i tapetowanie',
      'Dokładność w pomiarach',
      'Przygotowywanie zapraw'
    ],
    magazynier: [
      'Znajomość zasad organizacji i inwentaryzacji magazynu',
      'Obsługa systemów magazynowych',
      'Sprawność fizyczna',
      'Dobra organizacja czasu pracy'
    ],
    operator_maszyn: [
      'Obsługa i konserwacja maszyn rolniczych',
      'Znajomość przepisów ruchu drogowego',
      'Gotowość do pracy w zmiennych warunkach',
      'Praca w długich godzinach'
    ],
    rolnik: [
      'Obsługa maszyn rolniczych',
      'Znajomość cykli upraw i podstaw agrotechniki',
      'Gotowość do pracy sezonowej',
      'Praca w zmiennych warunkach atmosferycznych'
    ],
    rybak: [
      'Znajomość sprzętu i technik połowu',
      'Odporność na warunki atmosferyczne',
      'Gotowość do pracy fizycznej',
      'Zasady bezpieczeństwa na wodzie'
    ]
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomCompetencyChange = (index, value) => {
    const newCompetencies = [...formData.customCompetencies];
    newCompetencies[index] = value;
    setFormData(prev => ({
      ...prev,
      customCompetencies: newCompetencies
    }));
  };

  const addCustomCompetency = () => {
    setFormData(prev => ({
      ...prev,
      customCompetencies: [...prev.customCompetencies, '']
    }));
  };

  const removeCustomCompetency = (index) => {
    const newCompetencies = formData.customCompetencies.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      customCompetencies: newCompetencies
    }));
  };

  const generateLetter = () => {
    const today = new Date();
    const formattedDate = `${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}`;
    
    const professionLabel = formData.profession === 'custom' 
      ? formData.customProfession 
      : professions.find(p => p.value === formData.profession)?.label || '';
    
    const skills = formData.profession === 'custom'
      ? formData.customCompetencies.filter(c => c.trim())
      : competencies[formData.profession] || [];

    // ZMIANA: Zaktualizowany szablon z oddzielnym telefonem i e-mailem
    return `
${formData.fullName}
${formData.address}
${formData.city}
tel. ${formData.phone}
email: ${formData.email}

${formData.city}, dnia ${formattedDate}

Do ${formData.companyName}
${formData.employerName}

Zgłoszenie kandydata na stanowisko ${formData.position}

Szanowni Państwo,

Piszę do Państwa w odpowiedzi na ogłoszenie dotyczące stanowiska ${formData.position} lub w związku z moją chęcią podjęcia pracy w Państwa firmie.

Jako świeżo upieczony absolwent Szkoły Branżowej I Stopnia w zawodzie ${professionLabel}, z tytułem zawodowym, jestem gotów wnieść do Państwa zespołu świeżą energię i solidne, praktyczne umiejętności.

Moją największą zaletą jest bogate doświadczenie praktyczne, nabyte w trakcie 3 lat nauki zawodu${formData.practicePlace ? ` w ${formData.practicePlace}` : ''}.

W trakcie praktycznej nauki zawodu:
${skills.map(skill => `• ${skill}`).join('\n')}

${formData.achievements ? `\n${formData.achievements}\n` : ''}

To doświadczenie sprawia, że nie jestem tylko teoretykiem, ale osobą, która potrafi szybko wdrożyć się do pracy i rozumie realia Państwa firmy.

Jestem młodą, kreatywną i ambitną osobą, która postrzegam Państwa firmę jako idealne miejsce do dalszego rozwoju. Zależy mi na zdobywaniu nowej wiedzy i doskonaleniu moich umiejętności pod okiem doświadczonej kadry.

${formData.motivation ? `${formData.motivation}\n\n` : ''}

Jednocześnie wierzę, że moja pasja do zawodu oraz potwierdzone tytułem zawodowym kompetencje mogą stanowić realne wsparcie dla Państwa załogi od pierwszego dnia pracy.

Z chęcią spotkam się z Państwem osobiście, aby szczegółowo omówić moje kwalifikacje oraz przedstawić moją wizję wkładu w Państwa przedsiębiorstwo.

Z wyrazami szacunku,
${formData.fullName}
    `.trim();
  };

  const downloadLetter = () => {
    const letter = generateLetter();
    const blob = new Blob([letter], { type: 'text/plain;charset=utf-8' });
    const safeName = (formData.fullName || 'list').replace(/\s+/g, '_').replace(/[^\w\-_.]/g, '');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `list_motywacyjny_${safeName}.txt`;
    link.click();
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  // ZMIANA: Walidacja na 'phone' i 'email'
  const isStep1Valid = formData.fullName && formData.address && formData.city && formData.phone && formData.email;
  const isStep2Valid = formData.companyName && formData.position;
  const isStep3Valid = formData.profession && 
    (formData.profession !== 'custom' || 
     (formData.customProfession.trim() && formData.customCompetencies.filter(c => c.trim()).length >= 2));

     return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">
        
        {/* HERO SECTION */}
        <motion.div {...fadeInUp} className="text-center mb-5">
          <h1 className="display-4 fw-bold text-white mb-3">
            Generator Listu Motywacyjnego
          </h1>
          <p className="lead text-white-50 mb-4">
            Stwórz profesjonalny list motywacyjny w kilka minut
          </p>          
          {/* Progress Steps */}
          <CoverLetterProgress step={step} />
        </motion.div>

        <AnimatePresence mode="wait">
          {/* KROK 1: Dane osobowe */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Row className="justify-content-center">
                <Col xs={12} lg={8}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{ borderRadius: '25px', overflow: 'hidden' }}
                  >
                    <div
                      className="p-4"
                      style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)' }}
                    >
                      <div className="d-flex align-items-center">
                        <h5 className="text-white mb-0">Krok 1: Twoje dane osobowe</h5>
                      </div>
                    </div>
                    <Card.Body className="p-4">                     
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">Imię i nazwisko</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. Jan Kowalski"
                            value={formData.fullName}
                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">Adres zamieszkania</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. ul. Kwiatowa 15/3"
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">Miejscowość</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. Poznań"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">Numer telefonu</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="np. 123-456-789"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-semibold">Adres e-mail</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="np. jan@example.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>
                      </Form>
                    </Card.Body>
                  </Card>
                <div className="row g-3 mt-3">
                  <div className="d-flex justify-content-end">
                    <GradientButton
                      onClick={() => setStep(2)}
                      disabled={!isStep1Valid}
                      gradient="blue"
                    >
                      Dalej
                      <FaArrowRight className="ms-2" />
                    </GradientButton>
                  </div>
                </div>
                </Col>
              </Row>
            </motion.div>
          )}

          {/* KROK 2: Dane pracodawcy */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Row className="justify-content-center">
                <Col xs={12} lg={8}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{ borderRadius: '25px', overflow: 'hidden' }}
                  >
                    <div
                      className="p-4"
                      style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)' }}
                    >
                      <div className="d-flex align-items-center">
                        <h5 className="text-white mb-0">Krok 2: Dane pracodawcy</h5>
                      </div>
                    </div>
                    <Card.Body className="p-4">
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">Nazwa firmy</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. Cukiernia Słodki Kąsek"
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-semibold">
                            Imię i nazwisko właściciela/kierownika (opcjonalnie)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. Pan Jan Nowak"
                            value={formData.employerName}
                            onChange={(e) => handleInputChange('employerName', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-semibold">Stanowisko, o które aplikujesz</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="np. Młodszy Cukiernik / Pomocnik Cukiernika"
                            value={formData.position}
                            onChange={(e) => handleInputChange('position', e.target.value)}
                            style={{
                              borderRadius: '15px',
                              border: '2px solid #e0e0e0',
                              padding: '0.75rem'
                            }}
                          />
                        </Form.Group>
                      </Form>
                    </Card.Body>
                  </Card>
                  <div className="row g-3 mt-3">
                    <div className="d-flex justify-content-between">
                      <GradientButton
                        onClick={() => setStep(1)}
                        gradient="pink"
                      >
                        <FaArrowLeft className="me-2" />
                        Wstecz                            
                      </GradientButton>
                      <GradientButton
                        onClick={() => setStep(3)}
                        disabled={!isStep2Valid}
                        gradient="blue"
                      >
                        Dalej
                        <FaArrowRight className="ms-2" />
                      </GradientButton>
                    </div>
                  </div>
                </Col>
              </Row>
            </motion.div>
          )}

          {/* KROK 3: Wybór zawodu */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Row className="justify-content-center">
                <Col xs={12} lg={10}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{ borderRadius: '25px', overflow: 'hidden' }}
                  >
                    <div
                      className="p-4"
                      style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)' }}
                    >
                      <h5 className="text-white mb-0">Krok 3: Wybierz zawód i uzupełnij szczegóły</h5>
                    </div>
                    <Card.Body className="p-4">
                      <Form>
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-semibold mb-3">Wybierz swój zawód</Form.Label>
                          <Row className="g-3">
                            {professions.map((prof) => (
                              <Col key={prof.value} xs={6} md={4} lg={3}>
                                <div
                                  onClick={() => handleInputChange('profession', prof.value)}
                                  style={{
                                    padding: '1rem',
                                    borderRadius: '15px',
                                    border: formData.profession === prof.value
                                      ? '3px solid var(--bs-primary)'
                                      : '2px solid #e0e0e0',
                                    background: formData.profession === prof.value
                                      ? 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
                                      : 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    textAlign: 'center',
                                    height: '100%'
                                  }}
                                >
                                  <div style={{ fontSize: '2rem' }} className="mb-2">{prof.icon}</div>
                                  <small className={
                                    formData.profession === prof.value 
                                    ? 'text-white fw-semibold' 
                                    : 'text-dark fw-semibold'
                                  }>
                                    {prof.label}
                                  </small>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>

                        {formData.profession && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                          >
                            {/* ... (bez zmian: custom profession, kompetencje, opcjonalne pola) ... */}
                            {formData.profession === 'custom' ? (
                              <>
                                <Form.Group className="mb-3">
                                  <Form.Label className="text-white fw-semibold">
                                    Nazwa zawodu
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="np. Technik informatyk"
                                    value={formData.customProfession}
                                    onChange={(e) => handleInputChange('customProfession', e.target.value)}
                                    style={{
                                      borderRadius: '15px',
                                      border: '2px solid #e0e0e0',
                                      padding: '0.75rem'
                                    }}
                                  />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                  <Form.Label className="text-white fw-semibold">
                                    Twoje kompetencje zawodowe (minimum 2)
                                  </Form.Label>
                                  {formData.customCompetencies.map((comp, index) => (
                                    <div key={index} className="d-flex align-items-center mb-2">
                                      <Form.Control
                                        type="text"
                                        placeholder={`Kompetencja ${index + 1}`}
                                        value={comp}
                                        onChange={(e) => handleCustomCompetencyChange(index, e.target.value)}
                                        style={{
                                          borderRadius: '15px',
                                          border: '2px solid #e0e0e0',
                                          padding: '0.75rem',
                                          marginRight: '0.5rem'
                                        }}
                                      />
                                      {formData.customCompetencies.length > 2 && (
                                        <Button
                                          variant="outline-danger"
                                          onClick={() => removeCustomCompetency(index)}
                                          style={{
                                            borderRadius: '10px',
                                            padding: '0.5rem 0.75rem'
                                          }}
                                        >
                                          <FaTimes />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                  <Button
                                    variant="outline-primary"
                                    onClick={addCustomCompetency}
                                    className="mt-2"
                                    style={{
                                      borderRadius: '10px',
                                      padding: '0.5rem 1rem'
                                    }}
                                  >
                                    <FaPlus className="me-2" />
                                    Dodaj kompetencję
                                  </Button>
                                </Form.Group>

                                {formData.customCompetencies.filter(c => c.trim()).length < 2 && (
                                  <Alert variant="warning" className="mb-3" style={{ borderRadius: '15px' }}>
                                    <small>⚠️ Dodaj minimum 2 kompetencje, aby przejść dalej</small>
                                  </Alert>
                                )}
                              </>
                            ) : (
                              <div
                                className="p-3 mb-4"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)',
                                  borderRadius: '15px',
                                  border: '2px solid rgba(67, 233, 123, 0.2)'
                                }}
                              >
                                <h6 className="text-white fw-bold mb-2">Twoje kompetencje zawodowe:</h6>
                                <ul className="mb-0">
                                  {competencies[formData.profession]?.map((comp, index) => (
                                    <li key={index} className="text-white mb-1">
                                      <small>{comp}</small>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <Form.Group className="mb-3">
                              <Form.Label className="text-white fw-semibold">
                                Miejsce praktycznej nauki zawodu (opcjonalnie)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="np. Cukiernia XYZ w Poznaniu"
                                value={formData.practicePlace}
                                onChange={(e) => handleInputChange('practicePlace', e.target.value)}
                                style={{
                                  borderRadius: '15px',
                                  border: '2px solid #e0e0e0',
                                  padding: '0.75rem'
                                }}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label className="text-white fw-semibold">
                                Dodatkowe osiągnięcia (opcjonalnie)
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="np. Uczestniczyłem/am w realizacji skomplikowanego projektu..."
                                value={formData.achievements}
                                onChange={(e) => handleInputChange('achievements', e.target.value)}
                                style={{
                                  borderRadius: '15px',
                                  border: '2px solid #e0e0e0',
                                  padding: '0.75rem'
                                }}
                              />
                            </Form.Group>

                            <Form.Group className="mb-4">
                              <Form.Label className="text-white fw-semibold">
                                Dodatkowa motywacja (opcjonalnie)
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Napisz dlaczego chcesz pracować w tej firmie..."
                                value={formData.motivation}
                                onChange={(e) => handleInputChange('motivation', e.target.value)}
                                style={{
                                  borderRadius: '15px',
                                  border: '2px solid #e0e0e0',
                                  padding: '0.75rem'
                                }}
                              />
                            </Form.Group>
                          </motion.div>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                  <div className="row g-3 mt-3">
                    <div className="d-flex justify-content-between">
                      <GradientButton
                        onClick={() => setStep(2)}
                        gradient="pink"
                      >
                        <FaArrowLeft className="me-2" />
                        Wstecz
                      </GradientButton>

                      <GradientButton
                        onClick={() => setStep(4)}
                        disabled={!isStep3Valid}
                        gradient="blue"
                      >
                        Podgląd
                      </GradientButton>
                    </div>
                  </div>
                </Col>
              </Row>
            </motion.div>
          )}

          {/* KROK 4: Podgląd i pobieranie */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Row className="justify-content-center">
                <Col xs={12} lg={10}>
                  <Card
                    className="border-0 shadow-lg mb-4"
                    style={{ borderRadius: '25px', overflow: 'hidden' }}
                  >
                    <div
                      className="p-4"
                      style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)' }}
                    >
                      <h5 className="text-white mb-0">Krok 4: Twój list motywacyjny</h5>
                    </div>
                    <Card.Body className="p-4">
                      <div
                        className="p-4 mb-4"
                        style={{
                          background: 'rgba(0,0,0,0.02)',
                          borderRadius: '15px',
                          border: '2px solid #e0e0e0',
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          whiteSpace: 'pre-wrap',
                          maxHeight: '500px',
                          overflowY: 'auto'
                        }}
                      >
                        {generateLetter()}
                      </div>

                      {/* ... (bez zmian: Alert ze wskazówkami) ... */}
                      <Alert
                        style={{
                          background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.1) 0%, rgba(183, 33, 255, 0.1) 100%)',
                          borderRadius: '15px',
                          border: '2px solid rgba(33, 212, 253, 0.2)'
                        }}
                      >
                        <strong className="text-white">Wskazówki:</strong>
                        <ul className="mb-0 mt-2">
                          <li className="text-white">
                            <small>Pobierz list jako plik tekstowy i otwórz go w programie do edycji tekstu (np. Word)</small>
                          </li>
                          <li className="text-white">
                            <small>Możesz jeszcze dostosować treść do swoich potrzeb</small>
                          </li>
                          <li className="text-white">
                            <small>Zadbaj o poprawną ortografię i formatowanie przed wysłaniem</small>
                          </li>
                          <li className="text-white">
                            <small>Wydrukuj list i podpisz go ręcznie przed złożeniem w formie papierowej</small>
                          </li>
                        </ul>
                      </Alert>
                    </Card.Body>
                  </Card>
                  <div className="row g-3 mt-3">
                    <div className="d-flex justify-content-between flex-wrap gap-2">
                      <GradientButton
                        onClick={() => setStep(3)}
                        gradient="pink"
                      >
                        <FaArrowLeft className="me-2" />
                        Wstecz
                      </GradientButton>

                      <div className="d-flex gap-2">
                        <GradientButton
                          onClick={resetForm}
                          gradient="purple"
                        >
                          Nowy list
                        </GradientButton>

                        <GradientButton
                          onClick={downloadLetter}
                          gradient="green"
                        >
                          <Download className="me-2" />
                          Pobierz plik
                        </GradientButton>
                      </div>
                    </div>
                  </div> 
                  <Card
                    className="border-0 shadow-lg mt-4"
                    style={{ borderRadius: '25px', overflow: 'hidden' }}
                  >
                    <div
                      className="p-4"
                      style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)' }}
                    >
                      <h5 className="text-white fw-bold mb-0">Dodatkowe porady</h5>
                    </div>
                    <Card.Body className="p-4">
                      <Row className="g-3">
                        <Col xs={12} md={4}>
                          <div
                            className="p-3 h-100"
                            style={{
                              background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)',
                              borderRadius: '15px',
                              border: '2px solid rgba(67, 233, 123, 0.2)'
                            }}
                          >
                            <h6 className="text-white fw-bold mb-2">✓ Formatowanie</h6>
                            <small className="text-white">
                              Zadbaj o czytelne odstępy między akapitami i jednolitą czcionkę
                            </small>
                          </div>
                        </Col>
                        <Col xs={12} md={4}>
                          <div
                            className="p-3 h-100"
                            style={{
                              background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.1) 0%, rgba(183, 33, 255, 0.1) 100%)',
                              borderRadius: '15px',
                              border: '2px solid rgba(33, 212, 253, 0.2)'
                            }}
                          >
                            <h6 className="text-white fw-bold mb-2">✓ Personalizacja</h6>
                            <small className="text-white">
                              Jeśli znasz osobę odpowiedzialną za rekrutację, wpisz jej imię i nazwisko
                            </small>
                          </div>
                        </Col>
                        <Col xs={12} md={4}>
                          <div
                            className="p-3 h-100"
                            style={{
                              background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
                              borderRadius: '15px',
                              border: '2px solid rgba(240, 147, 251, 0.2)'
                            }}
                          >
                            <h6 className="text-white fw-bold mb-2">✓ Podpis</h6>
                            <small className="text-white">
                              W wersji papierowej pamiętaj o czytelnym podpisie na końcu dokumentu
                            </small>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info section na dole */}
        {step < 4 && (
          <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="mt-5">
            <Row className="justify-content-center">
              <Col xs={12} md={8}>
                <div
                  className="p-4 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '20px',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <h6 className="text-white mb-2">Potrzebujesz pomocy?</h6>
                  <p className="text-white-50 mb-0">
                    <small>
                      Wypełnij wszystkie pola formularza krok po kroku, a system wygeneruje dla Ciebie
                      profesjonalny list motywacyjny dostosowany do Twojego zawodu.
                    </small>
                  </p>
                </div>
              </Col>
            </Row>
          </motion.div>
        )}

      </Container>
    </div>
  );
}

export default CoverLetter;